import React, { useMemo, useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { InputText } from "primereact/inputtext";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import {
  useEquipmentsByFilter,
  useCreateEquipmentMutation,
  useUpdateEquipmentMutation,
  useDeleteEquipmentMutation,
  useEquipmentAttributeDefinitions,
} from "presentation/hooks/use-query-equipments";
import { useEmployeesByFilter } from "presentation/hooks/use-query-employees";
import { useEquipmentTypeByFilterAll } from "presentation/hooks/use-query-equipment-type";
import { Equipment } from "domain/entities/equipment";
import { EquipmentInputFilter } from "domain/models/equipment-input-filter";
import Spinner from "presentation/components/Shared/Spinner";
import EquipmentForm from "./EquipmentForm";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import {
  calculateEquipmentCompleteness,
  filterEquipmentsByVehicleType,
  getVehicleEquipmentType,
  parseEquipmentAttributes,
} from "./equipment-utils";

interface AdminEquipmentsTabProps {
  showOnlyVehicles?: boolean;
}

const AdminEquipmentsTab: React.FC<AdminEquipmentsTabProps> = ({
  showOnlyVehicles = false,
}) => {
  const toast = useRef<Toast>(null);
  const [isDialogVisible, setIsDialogVisible] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState<Equipment>(
    {} as Equipment,
  );
  const [attributesObject, setAttributesObject] = useState<any>({});
  const [searchQuery, setSearchQuery] = useState("");
  const [localSearchQuery, setLocalSearchQuery] = useState("");
  const [lazyParams, setLazyParams] = useState<{
    page: number;
    limit: number;
    sortBy: NonNullable<EquipmentInputFilter["sortBy"]>;
    sortOrder: "asc" | "desc";
  }>({
    page: 1,
    limit: 50,
    sortBy: "createdAt",
    sortOrder: "desc",
  });

  const { data: eqTypesResponse } = useEquipmentTypeByFilterAll({
    limit: 100,
  } as any);
  const viaturaType = getVehicleEquipmentType(eqTypesResponse?.data);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setSearchQuery(localSearchQuery);
      setLazyParams((current) => ({
        ...current,
        page: 1,
      }));
    }
  };

  const { data: response, isLoading } = useEquipmentsByFilter(
    {
      page: lazyParams.page,
      limit: lazyParams.limit,
      sortBy: lazyParams.sortBy,
      sortOrder: lazyParams.sortOrder,
      query: searchQuery,
      equipmentTypeId: showOnlyVehicles ? viaturaType?.id : undefined,
    },
    !showOnlyVehicles || !!viaturaType?.id,
  );

  const { data: employeesResponse } = useEmployeesByFilter({ limit: 200 });
  const { data: allAttrDefsResponse } = useEquipmentAttributeDefinitions({});

  const employeesById = useMemo(() => {
    const map = new Map<string, { name?: string }>();
    (employeesResponse?.data || []).forEach((employee) => {
      if (employee.id) {
        map.set(String(employee.id).trim().toLowerCase(), employee);
      }
    });
    return map;
  }, [employeesResponse?.data]);

  const createMutation = useCreateEquipmentMutation();
  const updateMutation = useUpdateEquipmentMutation();
  const deleteMutation = useDeleteEquipmentMutation();

  const handleCloseDialog = () => {
    setIsDialogVisible(false);
    setSelectedEquipment({} as Equipment);
    setAttributesObject({});
  };

  const handleOpenNew = () => {
    setSelectedEquipment({
      equipmentTypeId: "",
      brand: "",
      model: "",
      serialOrCarRegistration: "",
      status: "IN_STOCK",
      attributes: "{}",
      supplier: "",
      invoice: "",
      purchaseDate: null,
      warrantyMonths: null,
    } as Equipment);
    setAttributesObject({});
    setIsDialogVisible(true);
  };

  const handleEdit = (equipment: Equipment) => {
    setSelectedEquipment({ ...equipment });
    setAttributesObject(parseEquipmentAttributes(equipment.attributes));
    setIsDialogVisible(true);
  };

  const handleDelete = (id: string) => {
    confirmDialog({
      message:
        "Tem a certeza que deseja eliminar permanentemente este equipamento do inventário?",
      header: "Confirmação de Eliminação",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      acceptLabel: "Sim",
      rejectLabel: "Não",
      accept: () => {
        deleteMutation.mutate(id, {
          onSuccess: () => {
            toast.current?.show({
              severity: "success",
              summary: "Sucesso",
              detail: "Equipamento removido.",
              life: 3000,
            });
          },
          onError: (err: any) => {
            toast.current?.show({
              severity: "error",
              summary: "Erro",
              detail: err.message,
              life: 3000,
            });
          },
        });
      },
    });
  };

  const handleSave = () => {
    const payload = {
      ...selectedEquipment,
      attributes: JSON.stringify(attributesObject),
    };

    if (selectedEquipment.id) {
      updateMutation.mutate(payload, {
        onSuccess: () => {
          toast.current?.show({
            severity: "success",
            summary: "Sucesso",
            detail: "Equipamento guardado.",
            life: 3000,
          });
          handleCloseDialog();
        },
        onError: (err: any) => {
          toast.current?.show({
            severity: "error",
            summary: "Erro",
            detail: err.message,
            life: 3000,
          });
        },
      });
    } else {
      if (
        selectedEquipment.status === "IN_USE" &&
        !selectedEquipment.employeeId
      ) {
        toast.current?.show({
          severity: "warn",
          summary: "Movimento Inicial Obrigatório",
          detail:
            "Antes de guardar, registe o movimento inicial de Entrega/Alocação na tab Movimentos.",
          life: 4000,
        });
        return;
      }

      if (selectedEquipment.employeeId) {
        const type = eqTypesResponse?.data?.find(
          (t) => t.id === selectedEquipment.equipmentTypeId,
        );
        const minRequired = type?.minDeliveryPhotos || 0;
        let uploadedPhotos = [];
        const parsedPhotos = parseEquipmentAttributes(selectedEquipment.photos);
        uploadedPhotos = Array.isArray(parsedPhotos) ? parsedPhotos : [];
        if (uploadedPhotos.length < minRequired) {
          toast.current?.show({
            severity: "warn",
            summary: "Fotos Obrigatórias",
            detail: `São necessárias pelo menos ${minRequired} foto(s) para a alocação inicial deste equipamento.`,
            life: 4000,
          });
          return;
        }
      }

      createMutation.mutate(payload, {
        onSuccess: () => {
          toast.current?.show({
            severity: "success",
            summary: "Sucesso",
            detail: "Equipamento guardado.",
            life: 3000,
          });
          handleCloseDialog();
        },
        onError: (err: any) => {
          toast.current?.show({
            severity: "error",
            summary: "Erro",
            detail: err.message,
            life: 3000,
          });
        },
      });
    }
  };

  const actionBodyTemplate = (rowData: Equipment) => {
    return (
      <div className="flex gap-2">
        <Button
          icon="pi pi-pencil" severity="secondary"
         
          className="p-button-rounded p-button-text"
          onClick={() => handleEdit(rowData)}
        />
        <Button
          icon="pi pi-trash" severity="secondary"
         
          className="p-button-rounded p-button-text"
          onClick={() => handleDelete(rowData.id!)}
        />
      </div>
    );
  };

  const equipmentTypeBodyTemplate = (rowData: Equipment) => {
    const eqType = eqTypesResponse?.data?.find(
      (t) => t.id === rowData.equipmentTypeId,
    );
    return eqType?.name || rowData.equipmentTypeId;
  };

  const employeeNameBodyTemplate = (rowData: Equipment) => {
    const apiEmployeeName =
      rowData.employee?.name?.trim() || rowData.employeeName?.trim();
    if (apiEmployeeName) return apiEmployeeName;

    if (!rowData.employeeId) return "Não alocado";
    const normalizedEmployeeId = String(rowData.employeeId)
      .trim()
      .toLowerCase();
    const employee = employeesById.get(normalizedEmployeeId);

    if (employee?.name) return employee.name;
    if (!employeesResponse?.data) return "A carregar...";
    return "Colaborador não encontrado";
  };

  const completenessBodyTemplate = (rowData: Equipment) => {
    const pct = calculateEquipmentCompleteness(
      rowData,
      allAttrDefsResponse?.data?.data,
    );
    let color = "#22c55e"; // verde
    if (pct < 50) {
      color = "#ef4444"; // vermelho
    } else if (pct < 100) {
      color = "#f59e0b"; // amarelo
    }

    return (
      <div className="flex align-items-center gap-2">
        <div
          className="bg-gray-200 rounded-full"
          style={{
            backgroundColor: "#e5e7eb",
            borderRadius: "9999px",
            width: "4rem",
            height: "6px",
            overflow: "hidden",
          }}
        >
          <div
            style={{ width: `${pct}%`, backgroundColor: color, height: "100%" }}
          ></div>
        </div>
        <span style={{ fontWeight: "bold", color, fontSize: "0.85rem" }}>
          {pct}%
        </span>
      </div>
    );
  };

  if (isLoading) return <Spinner />;

  const statusOptions = [
    { value: "IN_USE", label: "Em uso" },
    { value: "IN_MAINTENANCE", label: "Em manutenção" },
    { value: "IN_STOCK", label: "Em stock" },
    { value: "LOST", label: "Perdido" },
    { value: "OBSOLETE", label: "Obsoleto" },
  ];

  const filteredData = filterEquipmentsByVehicleType(
    response?.data?.data,
    viaturaType?.id,
    showOnlyVehicles,
  );

  const totalRecords = response?.data?.count || filteredData.length;

  return (
    <div>
      <div className="flex justify-content-between align-items-center mb-3">
        <IconField iconPosition="left" className="w-full md:w-20rem">
          <InputIcon className="pi pi-search" />
          <InputText
            value={localSearchQuery}
            onChange={(e) => setLocalSearchQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Pesquisa global..."
            className="w-full"
          />
        </IconField>
        <Button
          label={showOnlyVehicles ? "Nova Viatura" : "Novo Equipamento"}
          icon="pi pi-plus"
          onClick={handleOpenNew}
        />
      </div>

      <DataTable
        lazy
        value={filteredData}
        paginator
        first={(lazyParams.page - 1) * lazyParams.limit}
        rows={lazyParams.limit}
        totalRecords={totalRecords}
        onPage={(event) =>
          setLazyParams({
            page: (event.page ?? 0) + 1,
            limit: event.rows,
            sortBy: lazyParams.sortBy,
            sortOrder: lazyParams.sortOrder,
          })
        }
        sortField={lazyParams.sortBy}
        sortOrder={lazyParams.sortOrder === "asc" ? 1 : -1}
        onSort={(event) =>
          setLazyParams((current) => ({
            ...current,
            page: 1,
            sortBy: (event.sortField ||
              "createdAt") as NonNullable<EquipmentInputFilter["sortBy"]>,
            sortOrder: event.sortOrder === 1 ? "asc" : "desc",
          }))
        }
      >
        {!showOnlyVehicles && (
          <Column
            field="equipmentTypeId"
            header="Tipo de Equipamento"
            body={equipmentTypeBodyTemplate}
            sortable
          />
        )}
        <Column field="brand" header="Marca" sortable />
        <Column
          field="model"
          header="Modelo"
          body={(rowData: Equipment) => (
            <div className="flex align-items-center gap-2">
              <span>{rowData.model}</span>
              {rowData.hasPhotos && (
                <span title="Tem fotos associadas">📸</span>
              )}
            </div>
          )}
          sortable
        />
        <Column
          field="serialOrCarRegistration"
          header={showOnlyVehicles ? "Matrícula" : "Nº Série"}
          sortable
        />
        <Column
          field="employeeId"
          header="Alocado a"
          body={employeeNameBodyTemplate}
          sortable
        />
        <Column
          field="status"
          header="Status"
          body={(rowData: Equipment) => {
            const status = rowData.status;
            const label = statusOptions.find((o) => o.value === status)?.label || status;
            let severity: "success" | "warning" | "danger" | "info" = "info";
            let customStyle: React.CSSProperties = {
              fontWeight: "600",
              fontSize: "0.85rem",
              padding: "0.25rem 0.6rem"
            };

            if (status === "IN_USE") {
              severity = "success";
              customStyle = { ...customStyle, backgroundColor: "#eafaf1", color: "#27ae60", border: "1px solid #c2f0d1" };
            } else if (status === "IN_MAINTENANCE") {
              severity = "warning";
              customStyle = { ...customStyle, backgroundColor: "#fff3e6", color: "#c26000", border: "1px solid #ffe0b2" };
            } else if (status === "IN_STOCK") {
              severity = "info";
              customStyle = { ...customStyle, backgroundColor: "#ebf5fe", color: "#1e40af", border: "1px solid #bfdbfe" };
            } else if (status === "LOST") {
              severity = "danger";
              customStyle = { ...customStyle, backgroundColor: "#fdf2f2", color: "#c81e1e", border: "1px solid #fde8e8" };
            } else if (status === "OBSOLETE") {
              severity = "info";
              customStyle = { ...customStyle, backgroundColor: "#f3f4f6", color: "#374151", border: "1px solid #e5e7eb" };
            }

            return <Tag value={label} severity={severity} rounded style={customStyle} />;
          }}
          sortable
        />
        <Column header="Preenchimento" body={completenessBodyTemplate} />
        <Column
          header="Ações"
          body={actionBodyTemplate}
          style={{ width: "8rem" }}
        />
      </DataTable>

      <Toast ref={toast} />

      <Dialog
        header={
          selectedEquipment.id
            ? showOnlyVehicles
              ? "Editar Viatura"
              : "Editar Equipamento"
            : showOnlyVehicles
              ? "Registar Viatura"
              : "Registar Equipamento"
        }
        visible={isDialogVisible}
        style={{ width: "60vw" }}
        contentStyle={{ minHeight: "450px" }}
        onHide={handleCloseDialog}
        footer={
          <div>
            <Button
              label="Cancelar"
              icon="pi pi-times"
              onClick={handleCloseDialog}
              className="p-button-text"
            />
            <Button
              label="Guardar"
              icon="pi pi-check"
              onClick={handleSave}
              autoFocus
            />
          </div>
        }
      >
        <EquipmentForm
          selectedEquipment={selectedEquipment}
          setSelectedEquipment={setSelectedEquipment}
          attributesObject={attributesObject}
          setAttributesObject={setAttributesObject}
          isAdmin={true}
          readOnlyBaseFields={false}
          showOnlyVehicles={showOnlyVehicles}
        />
      </Dialog>
      <ConfirmDialog />
    </div>
  );
};

export default AdminEquipmentsTab;
